"use client";
import {
  ZSL,
  canvasAtom,
  isEditDescriptionModalOpenAtom,
  isKeyboardShortcutsModalOpenAtom,
  isDeleteCanvasModalOpenAtom,
  BuiltInApp,
  Session,
  Account,
  Toggles,
  RawCanvas,
} from "@zapier/canvas-state";
import { DesignSystemProvider } from "@zapier/design-system";
import { useAtomValue, useAtom } from "jotai";
import { ReactNode } from "react";
import { ReactFlowProvider } from "@xyflow/react";
import { useSearchParams } from "next/navigation";

import { useBindUndoRedoKeyboardShortcuts } from "../../hooks/useBindUndoRedoKeyboardShortcuts";
import useUpdateSelectedApps from "../../hooks/useUpdateSelectedApps";
import { useCopilotToolMessageListener } from "../../hooks/useCopilotToolMessageListener";
import { TitleUpdater } from "../TitleUpdater";
import KeyboardShortcutsModal from "../KeyboardShortcutsModal/KeyboardShortcutsModal";
import { Notifications } from "../Notifications";
import { EditDescriptionModal } from "../modals/EditDescriptionModal";
import { DeleteModal } from "../modals/DeleteModal";
import { CanvasHydrator } from "../CanvasHydrator";
import { CopilotProvider } from "../CopilotProvider";
import { BuilderNav } from "../BuilderNav";

import { useEmitPageViewEvent } from "./useEmitPageViewEvent";

type Props = {
  children: ReactNode;
  canvas?: RawCanvas;
  template?: ZSL;
  tablesApp: BuiltInApp;
  interfacesApp: BuiltInApp;
  chatbotsApp: BuiltInApp;
  agentsApp: BuiltInApp;
  account: Account;
  session: Session;
  toggles: Toggles;
  isAgentsEnabled: boolean;
};

export default function CanvasLayoutWrapper({
  children,
  canvas: canvasProp,
  template,
  tablesApp,
  interfacesApp,
  chatbotsApp,
  agentsApp,
  account,
  session,
  toggles,
  isAgentsEnabled,
}: Props) {
  const searchParams = useSearchParams();
  const prompt = searchParams?.get("prompt") || undefined;
  const noShowModal = searchParams?.get("noShowModal");
  const canvas = canvasProp ?? (template?.canvases[0] as RawCanvas);

  return (
    <DesignSystemProvider>
      <ReactFlowProvider>
        <CanvasHydrator
          canvas={canvas}
          account={account}
          session={session}
          template={template}
          tablesApp={tablesApp}
          interfacesApp={interfacesApp}
          chatbotsApp={chatbotsApp}
          agentsApp={agentsApp}
          toggles={toggles}
          isAgentsEnabled={isAgentsEnabled}
          prompt={prompt}
          noShowModal={noShowModal}
        >
          <Notifications />
          <CopilotProvider>
            <CanvasLayout>{children}</CanvasLayout>
          </CopilotProvider>
        </CanvasHydrator>
      </ReactFlowProvider>
    </DesignSystemProvider>
  );
}

function CanvasLayout({ children }: { children: ReactNode }) {
  useEmitPageViewEvent();

  const [isEditDescriptionModalOpen, setIsEditDescriptionModalOpen] = useAtom(
    isEditDescriptionModalOpenAtom,
  );

  const [isDeleteCanvasModalOpen, setIsDeleteCanvasModalOpen] = useAtom(
    isDeleteCanvasModalOpenAtom,
  );
  useCopilotToolMessageListener();

  const [isKeyboardShortcutsModalOpen, setIsKeyboardShortcutsModalOpen] =
    useAtom(isKeyboardShortcutsModalOpenAtom);

  const currentCanvas = useAtomValue(canvasAtom);
  useUpdateSelectedApps();
  useBindUndoRedoKeyboardShortcuts();

  return (
    <>
      <TitleUpdater title={currentCanvas.name || "Canvas"} />
      <BuilderNav>{children}</BuilderNav>
      {isEditDescriptionModalOpen && (
        <EditDescriptionModal
          onClosed={() => setIsEditDescriptionModalOpen(false)}
        />
      )}
      {isDeleteCanvasModalOpen && (
        <DeleteModal onClosed={() => setIsDeleteCanvasModalOpen(false)} />
      )}
      {isKeyboardShortcutsModalOpen && (
        <KeyboardShortcutsModal
          onClosed={() => setIsKeyboardShortcutsModalOpen(false)}
        />
      )}
    </>
  );
}
